import { getAsyncResult, type AsyncRequestResult } from '@/lib/api/async-request.api';
import type { ApiError } from '@/lib/api-common/apiError';

export type AsyncRequestTerminal<T> =
  | AsyncRequestResult<T>
  | { status: 'client-timedout' }
  | { status: 'lost'; errorMessage: string };

export interface PollAsyncRequestOptions<T> {
  /** ISO string / epoch ms / Date the BE gave back when the request was enqueued. */
  timeoutAt: string | number | Date;
  /** Poll interval in ms. Default 4000 — this has a user actively watching for
   *  a result, unlike this codebase's other bespoke background pollers
   *  (import.tsx, campaigns/index.tsx), which check every 15-30s in the background. */
  intervalMs?: number;
  /** Consecutive transient poll failures (network blips) tolerated before
   *  giving up as 'lost'. A 400 (request no longer exists) gives up immediately
   *  regardless of this counter — retrying won't bring a purged doc back. */
  maxConsecutiveFailures?: number;
  onTerminal: (result: AsyncRequestTerminal<T>) => void;
}

/**
 * Polls `admin/async-request/get-result` for `requestId` until a terminal
 * state is reached, then calls `onTerminal` exactly once and stops.
 *
 * The BE only resolves a request to `timedout` when a delegation-server
 * worker actually claims it past its deadline — if no worker ever claims it
 * (worker down, or its task-name regexp doesn't match), `get-result` returns
 * `pending` forever. `timeoutAt` is therefore a required client-side backstop,
 * not optional polish: once passed, this stops polling and reports
 * `client-timedout` even though the BE itself never said so.
 *
 * Returns a cancel function; safe to call after the poll has already
 * terminated (no-op). Not a hook — see docs/instant-channel-update-
 * implementation-plan-20260801.md §2 for why a plain ref-keyed orchestrator
 * fits this codebase's row-per-map rendering better than `usePolling`.
 */
export function pollAsyncRequest<T = unknown>(
  requestId: string,
  opts: PollAsyncRequestOptions<T>,
): () => void {
  const intervalMs = opts.intervalMs ?? 4000;
  const maxConsecutiveFailures = opts.maxConsecutiveFailures ?? 3;
  const timeoutAtMs = new Date(opts.timeoutAt).getTime();

  let cancelled = false;
  let busy = false;
  let consecutiveFailures = 0;

  const finish = (result: AsyncRequestTerminal<T>) => {
    if (cancelled) return;
    cancelled = true;
    window.clearInterval(intervalId);
    opts.onTerminal(result);
  };

  const tick = () => {
    if (cancelled || busy) return;
    if (Date.now() > timeoutAtMs) {
      finish({ status: 'client-timedout' });
      return;
    }
    busy = true;
    getAsyncResult<T>(requestId)
      .then((res) => {
        busy = false;
        consecutiveFailures = 0;
        if (res.status === 'pending') return; // keep polling
        finish(res);
      })
      .catch((err: ApiError) => {
        busy = false;
        if (err?.code === 400) {
          // Request no longer exists (e.g. purged by the BE's periodic
          // cleanup) — retrying can't recover it.
          finish({ status: 'lost', errorMessage: err.message ?? 'Request not found' });
          return;
        }
        consecutiveFailures += 1;
        if (consecutiveFailures >= maxConsecutiveFailures) {
          finish({ status: 'lost', errorMessage: err?.message ?? 'Failed to poll update status' });
        }
      });
  };

  const intervalId = window.setInterval(tick, intervalMs);

  return () => {
    if (cancelled) return;
    cancelled = true;
    window.clearInterval(intervalId);
  };
}
